import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ComplianceBadge,
  EmptyState,
  Skeleton,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CheckCircle2, ClipboardCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  fetchComplianceDashboard,
  listRecentComplianceLogs,
} from '../../../api/compliance.js';

import { formatDate, outcomeLabel } from './lib.js';

export function ComplianceDashboardPage() {
  const dashQuery = useQuery({
    queryKey: ['compliance', 'dashboard'],
    queryFn: fetchComplianceDashboard,
  });
  const recentQuery = useQuery({
    queryKey: ['compliance', 'recent-logs'],
    queryFn: listRecentComplianceLogs,
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Overdue"
          tone="critical"
          icon={AlertTriangle}
          value={dashQuery.data?.counts.overdue}
          loading={dashQuery.isLoading}
          link="/compliance/records?status=overdue"
        />
        <Stat
          label="Due soon"
          tone="high"
          icon={CalendarClock}
          value={dashQuery.data?.counts.due_soon}
          loading={dashQuery.isLoading}
          link="/compliance/records?status=due_soon"
        />
        <Stat
          label="Upcoming"
          tone="muted"
          icon={ClipboardCheck}
          value={dashQuery.data?.counts.upcoming}
          loading={dashQuery.isLoading}
          link="/compliance/records?status=upcoming"
        />
        <Stat
          label="Logged"
          tone="success"
          icon={CheckCircle2}
          value={dashQuery.data?.counts.logged}
          loading={dashQuery.isLoading}
          link="/compliance/records?status=logged"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base">Most overdue</CardTitle>
            <Link to="/compliance/records?status=overdue" className="text-xs text-primary hover:underline">
              View all →
            </Link>
          </CardHeader>
          <CardContent>
            {dashQuery.isLoading ? (
              <SkeletonRows />
            ) : (dashQuery.data?.topOverdue.length ?? 0) === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title="Nothing overdue"
                description="Every required inspection has been logged on time."
              />
            ) : (
              <ul className="divide-y divide-border text-sm">
                {dashQuery.data!.topOverdue.map((row) => (
                  <li key={row.recordId} className="flex items-center justify-between gap-3 py-2">
                    <Link
                      to={`/compliance/records/${row.recordId}`}
                      className="flex min-w-0 flex-1 items-center gap-3 hover:underline"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{row.deviceLabel}</div>
                        <div className="truncate text-xs text-fg-muted">{row.buildingName}</div>
                      </div>
                    </Link>
                    <ComplianceBadge tone="critical">
                      {row.daysOverdue}d overdue
                    </ComplianceBadge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base">Coming up in 7 days</CardTitle>
            <Link to="/compliance/records?status=due_soon" className="text-xs text-primary hover:underline">
              View all →
            </Link>
          </CardHeader>
          <CardContent>
            {dashQuery.isLoading ? (
              <SkeletonRows />
            ) : (dashQuery.data?.upcomingNextSevenDays.length ?? 0) === 0 ? (
              <EmptyState
                icon={CalendarClock}
                title="Nothing due this week"
                description="The next 7 days are clear."
              />
            ) : (
              <ul className="divide-y divide-border text-sm">
                {dashQuery.data!.upcomingNextSevenDays.map((row) => (
                  <li key={row.recordId} className="flex items-center justify-between gap-3 py-2">
                    <Link
                      to={`/compliance/records/${row.recordId}`}
                      className="flex min-w-0 flex-1 items-center gap-3 hover:underline"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{row.deviceLabel}</div>
                        <div className="truncate text-xs text-fg-muted">{row.buildingName}</div>
                      </div>
                    </Link>
                    <span className="text-xs text-fg-muted tabular-nums">
                      in {row.daysUntilDue}d
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">Recent activity</CardTitle>
        </CardHeader>
        <CardContent>
          {recentQuery.isLoading ? (
            <SkeletonRows />
          ) : (recentQuery.data?.logs.length ?? 0) === 0 ? (
            <EmptyState
              icon={ClipboardCheck}
              title="No logs yet"
              description="As your team logs inspections, recent entries will show up here."
            />
          ) : (
            <ul className="divide-y divide-border text-sm">
              {recentQuery.data!.logs.map((row) => (
                <li
                  key={row.log.id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <Link
                    to={`/compliance/records/${row.record.id}`}
                    className="flex min-w-0 flex-1 items-center gap-3 hover:underline"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{row.device.label}</div>
                      <div className="truncate text-xs text-fg-muted">
                        {row.deviceType.name} · {outcomeLabel(row.log.outcome)}
                      </div>
                    </div>
                  </Link>
                  <span className="text-xs text-fg-muted">{formatDate(row.log.performedOn)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  loading,
  tone,
  icon: Icon,
  link,
}: {
  label: string;
  value: number | undefined;
  loading: boolean;
  tone: 'critical' | 'high' | 'muted' | 'success';
  icon: typeof AlertTriangle;
  link: string;
}) {
  return (
    <Link to={link}>
      <Card className="hover:border-primary">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {label}
            </span>
            <Icon className="h-4 w-4 text-fg-muted" />
          </div>
          {loading ? (
            <Skeleton className="mt-3 h-7 w-12" />
          ) : (
            <p className="mt-3 text-2xl font-semibold tabular-nums">{value ?? 0}</p>
          )}
          <div className="mt-2">
            <ComplianceBadge tone={tone}>{label}</ComplianceBadge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}
